'use client';

import { observer } from 'mobx-react-lite';
import IconCardRow from './icon-card-row';
import DescriptionList from './description-list';
import type { ExperienceDetailsListProps, ThingsToKnowBlock } from './types';
import { resolveHeadingLevel } from '../rich-text-title';
import BevSafeText from '../bev-safe-text';
import { useTranslations } from '@fshr/context/i18n-context';
import { detailsFromStore, findProduct, isAwaitingDetails } from './live-details';

function ExperienceDetailsList({
	sectionTitle = 'Experience Details',
	experienceDetailsHeadingLevel,
	thingsToKnowHeadingLevel,
	complimentary,
	durationTag,
	availability,
	timeOfDay,
	thingsToKnowBlocks,
	cancellationPolicy,
	userStore,
	pricingList,
	pricingListDisclaimer,
	productId,
	productAvailabilityStore,
}: ExperienceDetailsListProps) {
	const t = useTranslations();
	const storeProduct = findProduct(productAvailabilityStore, productId);
	const live = storeProduct ? detailsFromStore(storeProduct) : undefined;
	const awaitingDetails = isAwaitingDetails(productAvailabilityStore, productId);
	const areProductDetailsLoading = !live && awaitingDetails;

	// BL AC — Details section: the repeatable "Things To Know" blocks plus the
	// standalone "Cancellation Policy" field. The latter becomes one more section,
	// unless the author already authored one as a Things To Know block. These are
	// authored content and always come from props — never the availability store.
	const hasAuthoredCancellationBlock = thingsToKnowBlocks.some(
		block => block.type === 'cancellationPolicy'
	);
	const detailBlocks: ThingsToKnowBlock[] =
		cancellationPolicy?.trim() && !hasAuthoredCancellationBlock
			? [
					...thingsToKnowBlocks,
					{
						type: 'cancellationPolicy',
						// UI AC 3.c: not configurable.
						icon: 'no_available_date',
						title: t('Cancellation Policy'),
						body: [cancellationPolicy],
					},
				]
			: thingsToKnowBlocks;

	// BL AC — Duration tile: "Display value determined by set logic from Duration
	// Summary field" / "Override if value is set in the Duration Tag Text field",
	// which Blacklight ships as `durationOverride` ("Use to override the calculated
	// duration displayed in the Duration Tag").
	const resolvedPrice = live?.price;
	const resolvedComplimentary = live ? live.complimentary : complimentary;
	const resolvedTimeOfDay = live ? live.timeOfDay : timeOfDay;
	const resolvedAvailability = live ? live.availability : availability;
	const duration = live ? live.durationTag : durationTag;

	const pricePending = Boolean(productId) && !live && !resolvedComplimentary;

	// The price *value* is loading when the amount is still on its way — a product
	// whose store data has not arrived (`pricePending`), or a currency change that
	// is re-fetching an already-loaded tile (`awaitingDetails`). A resolved
	// `complimentary` flag or authored price is NOT loading, even with no store.
	const isPriceLoading = pricePending || awaitingDetails;

	// BL AC: default to H2, author may override.
	const SectionTitleTag = resolveHeadingLevel(experienceDetailsHeadingLevel, 'h2');
	const hasSectionTitle = Boolean(sectionTitle?.trim());

	return (
		// Figma "Experience Details" itemSpacing is bound to stack-xl in both
		// frames (30px mobile / 50px desktop).
		<section className="flex w-full flex-col gap-y-stack-xl">
			{hasSectionTitle && (
				<SectionTitleTag className="text-display3 text-text-heading">
					<BevSafeText>{sectionTitle}</BevSafeText>
				</SectionTitleTag>
			)}
			{/* No `userStore` guard: the tile row must render on the server. */}
			<IconCardRow
				price={resolvedPrice}
				complimentary={resolvedComplimentary}
				duration={duration}
				availability={resolvedAvailability}
				timeOfDay={resolvedTimeOfDay}
				isPriceLoading={isPriceLoading}
				areProductDetailsLoading={areProductDetailsLoading}
				pricePending={pricePending}
				currencyCode={userStore?.preferredCurrency}
				pricingList={live ? live.pricingList : pricingList}
				pricingListDisclaimer={live ? live.pricingListDisclaimer : pricingListDisclaimer}
			/>
			<DescriptionList blocks={detailBlocks} headingLevel={thingsToKnowHeadingLevel} />
		</section>
	);
}

export default observer(ExperienceDetailsList);
